import {FC, useEffect, useReducer, useState} from 'react'
import Logo from 'assets/images/DS-logo-white@2x.png'
import {useQueryClient} from '@tanstack/react-query'
import {useSelector} from 'react-redux'
import {Link} from 'react-router-dom'
import styled from 'styled-components'
import classNames from 'classnames'

import {dispatch} from 'app/store'
import {QuestionIcon, RenderIf, BellIcon} from 'components'
import {useNativeInfo, usePolicy, useSettingsDefaultPage} from 'hooks'
import {NavLink} from './TopNavLink'
import {
  selectUser,
  setImportStatusTimerRunning,
  userActions,
} from 'features/User'
import {selectedEndpointDebugMode} from 'features/Endpoints/endpointsSelectors'
import {
  GetImportStatusResponse,
  EndpointRoutesEnum,
  SSLType,
  PolicyRoutesEnum,
  CASBRoutesEnum,
  AccountList,
  AuthProviderEnum,
  InstallerType,
} from 'interfaces'
import {ArrowIcon, DownloadIcon} from 'components/Basic'
import {
  basePolicyRoutes,
  num0,
  secs10,
  settingsUsersRoute,
} from 'definitions/constant'
import {settingsActions} from 'features/Settings'
import HelpSection from './components/HelpSection'
import Toast from 'components/Basic/Toast'
import {history, isJWTTokenExpired, prefetchTranslations} from 'utils'
import {requestHandler, SettingsService} from 'services/api'
import {selectImportStatusState} from 'features/Settings/Users/userSelector'
import NotificationSection from './components/NotificationSection'
import {LogoutContainer} from './components/LogoutContainer'
import {InstallerDownloadMenu} from './components/InstallerDownloadMenu'

import {fetchNotifications} from './components/NotificationSection/service'
import {
  sslErrorActions,
  sslErrorNotificationInitState,
  sslErrorNotificationReducer,
} from './components/NotificationSection/reducer'
import {SSLErrorNotificationContext} from './components/NotificationSection/context'
import {prewarmEndpoints, useContextualPrefetch} from 'prefetch'
import {prewarmPolicies} from 'prefetch/prewarmPolicies'
import {CanIAccess, PERMISSIONS, withPermission} from 'rbac'
import {
  useGetImportStatusQuery,
  useGetUserImportLinkQuery,
} from 'features/Settings/Users/hooks'
import {EndPoints, HttpMethods} from 'services/api/HTTP/HTTP.types'
import Snackbar from 'components/Basic/Snackbar'

enum TopNavigationTab {
  none,
  nameTab,
  download,
  help,
  notification,
}

const NavContainer = styled.div`
  width: 100%;
  height: 3.5rem;
`

const importStatusTimerInterval = 10000
let importStatusInterval: ReturnType<typeof setInterval>
let notificationInterval: ReturnType<typeof setInterval>

interface TopNavigationProps {
  isForFallback?: boolean
}

const downloadInstallerData = [
  {
    labelKey: 'common:WINDOWS_DOWNLOAD',
    key: InstallerType.Windows,
  },
  {
    labelKey: 'common:WINDOWS_ARM_DOWNLOAD',
    key: InstallerType.WindowsArm,
  },
  {
    labelKey: 'common:APPLE_DOWNLOAD',
    key: InstallerType.Mac,
  },
]

const avoidRoutes = [
  '/preview',
  '/settings/grant-access',
  CASBRoutesEnum.GRANT_ACCESS,
  CASBRoutesEnum.ACCESS_GRANTED,
  CASBRoutesEnum.ACCESS_DENIED,
  CASBRoutesEnum.SYNC_FAILURE,
  '/settings',
  '/tenants',
  '/callback',
]

export const TopNavigation: FC<TopNavigationProps> = ({
  isForFallback = false,
}) => {
  const {pathname: currentRoute} = useNativeInfo()
  const {
    onboardingCompleted,
    name,
    emailId,
    downloadInstallerStatus,
    multiAccount,
    usersImported,
    isImportStatusTimerRunning,
    authProvider,
  } = useSelector(selectUser)
  const {
    data: {reauthRequired},
  } = useSelector(selectImportStatusState)

  const settingsDefaultPage = useSettingsDefaultPage()

  const queryClient = useQueryClient()

  const canReadNotifications = withPermission([PERMISSIONS.NOTIFICATIONS.READ])
  const canReadImportStatus = withPermission([PERMISSIONS.USER_MANAGEMENT.READ])

  const {prefetch} = useContextualPrefetch()

  const [currentRightTab, setCurrentRightTab] = useState<TopNavigationTab>(
    TopNavigationTab.none,
  )
  const [selectedBundle, setSelectedBundle] = useState<InstallerType | ''>('')
  const [selectedTenant, setSelectedTenant] = useState('')
  const [sslErrorNotificationState, sslErrorActionDispatcher] = useReducer(
    sslErrorNotificationReducer,
    sslErrorNotificationInitState,
  )

  // Microsoft user import re-auth polling state
  const [isReauthPolling, setIsReauthPolling] = useState(false)
  const [isGetImportStatusEnabled, setIsGetImportStatusEnabled] =
    useState(false)

  // creating separate local state for user import status
  // this state is being used to show re-auth required warning toast
  // this will help to common redux state overrides
  const [importStatusState, setImportStatusState] =
    useState<GetImportStatusResponse>({} as GetImportStatusResponse)
  const {isBasePolicyToBeColored} = usePolicy()
  const pathname = history.location.pathname

  const isDebugMode = useSelector(selectedEndpointDebugMode)

  const {data: userImportData} = useGetImportStatusQuery({
    enabled: isGetImportStatusEnabled,
    refetchInterval: isReauthPolling ? importStatusTimerInterval : undefined,
  })

  const {data: userImportLinkData} = useGetUserImportLinkQuery({
    enabled:
      Boolean(userImportData?.reauthRequiredNewApp) &&
      Boolean(!userImportData?.reauthRequired),
    refetchOnWindowFocus: userImportData?.reauthRequiredNewApp
      ? 'always'
      : false,
  })

  const onLogout = async (e: React.MouseEvent<HTMLElement>): Promise<void> => {
    e.stopPropagation()
    // If timer is running, stop it
    importStatusInterval && clearInterval(importStatusInterval)
    notificationInterval && clearInterval(notificationInterval)
    isImportStatusTimerRunning && dispatch(setImportStatusTimerRunning(false))
    setCurrentRightTab(TopNavigationTab.none)
    dispatch(userActions.logout())
  }

  useEffect(() => {
    const controller = new AbortController()
    const {signal: globalClickHandlerSignal} = controller

    const handleClick = (e: MouseEvent) => {
      const ids = ['logout', 'name', 'email', 'name icon', 'download-icon']
      const notificationEl = document.getElementById('notification-section')

      if (!ids.includes((e.target as HTMLElement)?.id ?? '')) {
        // safe way to close notification container without any regression
        if (notificationEl?.contains(e.target as Node)) {
          return
        }
        setCurrentRightTab(TopNavigationTab.none)
      }
    }

    window.addEventListener('click', handleClick, {
      signal: globalClickHandlerSignal,
    })
    return () => {
      controller.abort()
    }
  }, [])

  const onFileDownload = (bundle: InstallerType): void => {
    setSelectedBundle(bundle)
    dispatch(userActions.downloadInstaller({bundle}))
  }

  const onProfileClick = (e: React.MouseEvent<HTMLElement>): void => {
    e.stopPropagation()
    setCurrentRightTab(TopNavigationTab.nameTab)
  }

  const onAccountSwitch = async (
    e: React.MouseEvent<HTMLElement>,
    value: string,
  ): Promise<void> => {
    let token = multiAccount?.details?.token
    setSelectedTenant(value)
    e.stopPropagation()
    // Check if switch account token is expired or not
    if (isJWTTokenExpired(token)) {
      try {
        // If token is expired, refresh the token
        const {data} = await requestHandler<AccountList>({
          method: HttpMethods.GET,
          url: EndPoints.SwitchAccount,
        })
        token = data.token
      } catch (_error) {
        // Logout user if refresh switch account token fails
        dispatch(userActions.setTokensFailure())
      }
    }
    dispatch(
      userActions.setTokens({
        token,
        tenants: btoa(value),
        pageRefresh: true,
      }),
    )
  }

  const getSSLErrorDetails = async () => {
    sslErrorActionDispatcher(sslErrorActions.getSSLErrorNotifications())
    const result = await fetchNotifications({
      offset: num0,
      type: SSLType.app,
    })
    sslErrorActionDispatcher(
      sslErrorActions.getSSLErrorNotificationsSuccess(result),
    )
  }

  const startNotificationPolling = () => {
    if (!notificationInterval) {
      notificationInterval = setInterval(() => {
        getSSLErrorDetails()
      }, secs10)
    }
  }

  useEffect(() => {
    // prefetch common translation file on mount.
    // this change avoids page blink issue on SSL notification panel.
    prefetchTranslations(['common'])

    if (!isForFallback) {
      dispatch(userActions.getAccountsList())
    }
    return () => {
      setCurrentRightTab(TopNavigationTab.none)
      importStatusInterval && clearInterval(importStatusInterval)
      notificationInterval && clearInterval(notificationInterval)
      isImportStatusTimerRunning && dispatch(setImportStatusTimerRunning(false))
    }
  }, [isForFallback, isImportStatusTimerRunning])

  // this logic is stop polling when notification panel is visible
  useEffect(() => {
    if (!isForFallback) {
      // selected tab is not notification then start notification polling
      // else stop polling and do normal network call
      if (
        !notificationInterval &&
        currentRightTab !== TopNavigationTab.notification &&
        canReadNotifications
      ) {
        getSSLErrorDetails()
        startNotificationPolling()
      } else {
        notificationInterval && clearInterval(notificationInterval)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        notificationInterval = 0 as any
      }
    }
  }, [currentRightTab, canReadNotifications])

  useEffect(() => {
    const hasUserImportData = userImportData?.lastUpdated !== undefined
    const isReauthNewAppToastVisible =
      Boolean(userImportData?.reauthRequiredNewApp) &&
      Boolean(!userImportData?.reauthRequired)

    if (
      hasUserImportData &&
      isReauthPolling &&
      (userImportData?.reauthRequiredNewApp === false ||
        'reauthRequiredNewApp' in userImportData === false)
    ) {
      setIsReauthPolling(false)
      return
    }

    if (
      !isGetImportStatusEnabled &&
      (!hasUserImportData || isReauthPolling || isReauthNewAppToastVisible)
    ) {
      setIsGetImportStatusEnabled(true)
    }
  }, [
    userImportData?.reauthRequiredNewApp,
    userImportData?.reauthRequired,
    isReauthPolling,
  ])

  useEffect(() => {
    if (isImportStatusTimerRunning) {
      startPollingUserImport()
    }
  }, [isImportStatusTimerRunning])

  // If usersImported values changes to true, stop the timer
  // Set isImportStatusTimerRunning to false
  useEffect(() => {
    if (usersImported && !reauthRequired) {
      importStatusInterval && clearInterval(importStatusInterval)
      isImportStatusTimerRunning && dispatch(setImportStatusTimerRunning(false))
    }
  }, [usersImported, reauthRequired])

  // If usersImported and emailId values available means user is logged in.
  // Call import status api to verify the user import sync status.
  useEffect(() => {
    if (usersImported && emailId && canReadImportStatus) {
      void initGetImportStatusCall()
    }
  }, [usersImported, emailId, canReadImportStatus])

  const initGetImportStatusCall = async () => {
    try {
      const res = await SettingsService.getImportStatus()
      setImportStatusState(res?.data)
    } catch (_) {
      // no operation
    }
  }

  const startPollingUserImport = () => {
    // If usersImported is false
    // Start polling import status api every 5 seconds
    // Else clear the interval
    if (!usersImported || reauthRequired) {
      // Start timer only if no previous timer is present
      if (!importStatusInterval) {
        importStatusInterval = setInterval(() => {
          dispatch(settingsActions.getImportStatus())
        }, importStatusTimerInterval)
      }
    } else {
      importStatusInterval && clearInterval(importStatusInterval)
      isImportStatusTimerRunning && dispatch(setImportStatusTimerRunning(false))
    }
  }

  // on policy listing screen selected policy is set to base policy
  // to avoid any sort of regression policy listing screen this local var is useful.
  const isTopBarToBeBlueColor =
    (isBasePolicyToBeColored && basePolicyRoutes.includes(currentRoute)) ||
    isDebugMode

  const iconClick = (selectedTab: TopNavigationTab) => {
    currentRightTab === selectedTab
      ? setCurrentRightTab(TopNavigationTab.none)
      : setCurrentRightTab(selectedTab)
  }

  const onNavClick = (path: string): void => {
    switch (path) {
      case EndpointRoutesEnum.BASE:
        prewarmEndpoints(queryClient, prefetch)
        break
      case PolicyRoutesEnum.BASE:
        prewarmPolicies(prefetch)
        break
      default:
        break
    }
  }

  const handleUserImportLinkCopyFromSnackbar = () => {
    setIsReauthPolling(true)
  }

  return (
    <>
      <NavContainer
        className={classNames(
          'w-screen  flex justify-between items-center border-b-0.5 border-gray-400 relative px-6',
          {
            hidden: !onboardingCompleted || avoidRoutes.includes(currentRoute),
            'bg-dope-teal-100': isTopBarToBeBlueColor,
            'bg-dope-black': !isTopBarToBeBlueColor,
          },
        )}
        id="top-navigation"
        data-cy="top-navigation-bar"
      >
        {/* Check new reauth sync status and show reauth snackbar */}
        <RenderIf isTrue={!currentRoute?.includes('/settings/grant-access')}>
          <>
            <CheckUserImportStatus
              currentRoute={currentRoute}
              reAuthRequired={Boolean(importStatusState?.reauthRequired)}
            />
            <Snackbar
              show={
                authProvider === AuthProviderEnum.Microsoft &&
                Boolean(userImportData?.reauthRequiredNewApp) &&
                !userImportData?.reauthRequired
              }
              variant="warning"
              dismissible={true}
              copyText={userImportLinkData?.url}
              isCopyLoading={!userImportLinkData?.url}
              copyButtonText="Copy link"
              showCopy
              dataCy="microsoft-user-import-reauth-snackbar"
              onCopy={handleUserImportLinkCopyFromSnackbar}
            >
              {`It's time to update to our new Microsoft-verified app for enhanced security and performance. `}
              <a
                target="_blank"
                href={userImportLinkData?.url}
                className="text-dope-cyan-bright underline underline-offset-2"
                onClick={handleUserImportLinkCopyFromSnackbar}
              >
                {`Authorize now`}
              </a>
              {`, or share the link with your Microsoft 365 admin.`}
            </Snackbar>
          </>
        </RenderIf>
        <div className="flex items-center">
          <Link to="/analytics/overview" className="mr-7">
            <img src={Logo} alt="logo" className="object-contain w-8 h-8" />
          </Link>
          <NavLink
            pathname="/analytics/overview"
            current={pathname.includes('analytics')}
            linkName="Analytics"
          />
          <NavLink
            pathname="/policies"
            onClick={() => onNavClick(PolicyRoutesEnum.BASE)}
            current={pathname.includes('policies')}
            linkName="SWG"
          />
          <NavLink
            pathname={CASBRoutesEnum.BASE}
            current={pathname.includes('casb') || pathname.includes('sspm')}
            linkName="CASB"
          />
          <NavLink
            pathname="/endpoints"
            current={pathname === '/endpoints'}
            linkName="Endpoints"
            onClick={() => onNavClick(EndpointRoutesEnum.BASE)}
          />
          <NavLink
            pathname={settingsDefaultPage}
            current={pathname.includes('settings')}
            linkName="Settings"
          />
        </div>

        <ul className="flex items-center text-white abc-regular h-full">
          <CanIAccess reqPermissions={[PERMISSIONS.NOTIFICATIONS.READ]}>
            <li
              className={classNames('mr-4 h-full cursor-pointer', {
                'border-b-3 border-white':
                  currentRightTab === TopNavigationTab.notification,
              })}
            >
              <button
                data-cy="notification-bell-icon"
                aria-label="Click Notification Bell Icon to view SSL Pinning Errors"
                className="h-14 relative"
                onClick={e => {
                  e.stopPropagation()
                  iconClick(TopNavigationTab.notification)
                }}
              >
                <BellIcon classes="hover:text-white" />
                <RenderIf
                  isTrue={
                    sslErrorNotificationState?.totalAppCount > 0 ||
                    sslErrorNotificationState?.totalUrlCount > 0
                  }
                >
                  <span
                    data-cy="pending-notification-indicator"
                    className={classNames(
                      'h-2.5 w-2.5 flex justify-center items-center absolute right-0.5 top-3.5',
                      {
                        'bg-dope-teal-100': isTopBarToBeBlueColor,
                        'bg-dope-black': !isTopBarToBeBlueColor,
                      },
                    )}
                  >
                    <span className="relative h-1.5 w-1.5 flex">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-dope-purple-50 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-dope-purple-50"></span>
                    </span>
                  </span>
                </RenderIf>
              </button>
            </li>
          </CanIAccess>
          <li
            className={classNames('mr-4 h-full cursor-pointer', {
              'border-b-3 border-white':
                currentRightTab === TopNavigationTab.download,
            })}
          >
            <button
              id="download-icon"
              className="h-14"
              onClick={() => iconClick(TopNavigationTab.download)}
            >
              <DownloadIcon
                strokeColor="text-gray-1000"
                strokeWidth={1}
                id="download-icon"
                width={24}
                height={24}
                classes={'hover:text-white'}
              />
            </button>
          </li>
          <li
            className={classNames('mr-4 h-full cursor-pointer', {
              'border-b-3 border-white':
                currentRightTab === TopNavigationTab.help,
            })}
          >
            <button
              id="question-icon"
              className="h-14"
              onClick={e => {
                e.stopPropagation()
                iconClick(TopNavigationTab.help)
              }}
            >
              <QuestionIcon
                strokeColor="text-gray-1000"
                width={24}
                height={24}
                classes={'hover:text-white'}
              />
            </button>
          </li>
          <li
            className={classNames('cursor-pointer h-14 pt-4', {
              'border-b-3 border-white':
                currentRightTab === TopNavigationTab.nameTab,
            })}
          >
            <span
              id="name icon"
              data-cy="profile-icon"
              onClick={e =>
                currentRightTab === TopNavigationTab.nameTab
                  ? setCurrentRightTab(TopNavigationTab.none)
                  : onProfileClick(e)
              }
              className="rounded-full w-6 h-6 bg-dope-cyan-200 hover:bg-dope-cyan-100 flex justify-center items-center uppercase"
            >
              {name?.charAt(0) || '?'}
            </span>
          </li>
        </ul>
      </NavContainer>
      {(currentRightTab === TopNavigationTab.nameTab ||
        currentRightTab === TopNavigationTab.download ||
        currentRightTab === TopNavigationTab.help ||
        currentRightTab === TopNavigationTab.notification) && (
        <div
          data-cy="top-navigation-dropdown"
          className={classNames(
            'h-full z-40 fixed w-full top-14 bg-dope-black bg-opacity-0',
            {
              hidden:
                !onboardingCompleted || avoidRoutes.includes(currentRoute),
            },
          )}
        >
          <div className="grid-cols-14">
            <div className="col-start-3 grid grid-cols-14 col-span-14">
              <RenderIf
                isTrue={currentRightTab === TopNavigationTab.notification}
              >
                <SSLErrorNotificationContext.Provider
                  value={[sslErrorNotificationState, sslErrorActionDispatcher]}
                >
                  <NotificationSection
                    isTopBarToBeBlueColor={isTopBarToBeBlueColor}
                  />
                </SSLErrorNotificationContext.Provider>
              </RenderIf>
              <RenderIf isTrue={currentRightTab === TopNavigationTab.help}>
                <HelpSection isTopBarToBeBlueColor={isTopBarToBeBlueColor} />
              </RenderIf>
              <RenderIf isTrue={currentRightTab === TopNavigationTab.nameTab}>
                <LogoutContainer
                  name={name}
                  pathname={pathname}
                  emailId={emailId}
                  selectedTenant={selectedTenant}
                  onLogout={onLogout}
                  onAccountSwitch={onAccountSwitch}
                  isTopBarToBeBlueColor={isTopBarToBeBlueColor}
                />
              </RenderIf>
              <RenderIf isTrue={currentRightTab === TopNavigationTab.download}>
                <InstallerDownloadMenu
                  options={downloadInstallerData}
                  isTopBarToBeBlueColor={isTopBarToBeBlueColor}
                  downloadInstallerStatus={downloadInstallerStatus}
                  selectedBundle={selectedBundle}
                  onDownload={onFileDownload}
                />
              </RenderIf>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

interface CheckUserImportStatusProps {
  reAuthRequired: boolean
  currentRoute: string
}
const CheckUserImportStatus: FC<CheckUserImportStatusProps> = ({
  reAuthRequired,
  currentRoute,
}) => {
  const [showToastOnce, setShowToastOnce] = useState<boolean>(true)

  const canIManageUsers = withPermission([PERMISSIONS.USER_MANAGEMENT.WRITE])

  useEffect(() => {
    if (reAuthRequired && showToastOnce && canIManageUsers) {
      const isSettingsUsersPage = currentRoute === settingsUsersRoute
      const reAuthMessage = () => (
        <p>
          User import is no longer synced. Please reconnect to reinstate
          auto-sync of users.{' '}
          <RenderIf isTrue={!isSettingsUsersPage}>
            <span
              onClick={() => {
                history.push(settingsUsersRoute)
              }}
              className="text-yellow-100 cursor-pointer hover:underline decoration-0"
            >
              Take me there{' '}
              <ArrowIcon
                height={12}
                width={24}
                classes="text-yellow-100 inline-block"
                strokeWidth={0.1}
              />
            </span>
          </RenderIf>
        </p>
      )
      Toast({
        message: reAuthMessage,
        type: 'warning',
        style: {width: isSettingsUsersPage ? '700px' : '835px'},
      })
      setShowToastOnce(false)
    }
  }, [reAuthRequired, showToastOnce])

  return <></>
}
